/* ENA's original window illustrations. Shared geometry drives the site and GIF exports. */
(function (scope) {
  'use strict';
  const families = ['casement', 'sliding', 'bifold', 'folding-up'];
  const descriptions = {
    casement: 'Side-hinged sash swings outward, then closes.',
    sliding: 'One sash slides across the other, then closes.',
    bifold: 'Four linked panels fold to one side, then close.',
    'folding-up': 'Two linked panels fold upward, then close.'
  };
  const layouts = {
    casement: {x:150, y:64, width:178, height:254},
    sliding: {x:77, y:103, width:326, height:208},
    bifold: {x:53, y:113, width:374, height:193},
    'folding-up': {x:95, y:86, width:290, height:227}
  };
  const colors = {paper:'#eae9e2', frame:'#37483d', edge:'#26362d', highlight:'#617064', glass:'#c3d8d2', handle:'#21372b'};
  const clamp = n => Math.max(0, Math.min(1, Number(n) || 0));
  const project = (x,y,z=0) => [x + z * .29, y - z * .18];
  const rectangle = (x,y,w,h) => [[x,y],[x+w,y],[x+w,y+h],[x,y+h]];
  const polygon = (points,fill,opacity=1,part='') => ({points,fill,opacity,part});
  const round = n => Number(n.toFixed(3));
  const points = list => list.map(p=>p.map(round).join(',')).join(' ');

  // A 7.2-second cycle leaves time to see both the closed and open positions.
  function cycle(milliseconds) {
    const t = ((milliseconds % 7200) + 7200) % 7200 / 7200;
    const ease = n => n*n*(3-2*n);
    if (t < .16 || t >= .91) return {amount:0, label:'Closed'};
    if (t < .45) return {amount:ease((t-.16)/.29), label:'Opening'};
    if (t < .62) return {amount:1, label:'Open'};
    return {amount:1-ease((t-.62)/.29), label:'Closing'};
  }

  function geometry(family, amount=0) {
    if (!families.includes(family)) throw new Error('Unknown window opening style');
    const box = layouts[family], {x,y,width:w,height:h} = box, b = 9;
    const inside = {x:x+b,y:y+b,width:w-2*b,height:h-2*b};
    const a=clamp(amount), opening=[], panels=[];
    const shape=(coords,color,opacity=1,part='')=>opening.push(polygon(coords,color,opacity,part));
    // Neutral architectural surround and a soft garden view through the opening.
    shape(rectangle(0,0,480,400),colors.paper);
    shape([[x+w,y+12],[x+w+23,y+30],[x+w+23,y+h+24],[x+18,y+h+24],[x,y+h]],'#778370',.10);
    shape(rectangle(x+4,y+4,w+4,h+4),'#56684f',.12);
    shape(rectangle(x,y,w,h),colors.edge,1,'frame');
    shape(rectangle(inside.x,inside.y,inside.width,inside.height),'#dce5dc');
    shape([[x+b,y+h*.60],[x+w*.23,y+h*.51],[x+w*.41,y+h*.64],[x+w*.63,y+h*.47],[x+w-b,y+h*.58],[x+w-b,y+h-b],[x+b,y+h-b]],'#c0ccb7');
    shape([[x+b,y+h*.77],[x+w*.25,y+h*.65],[x+w*.47,y+h*.75],[x+w*.77,y+h*.61],[x+w-b,y+h*.69],[x+w-b,y+h-b],[x+b,y+h-b]],'#a9b99c',.7);
    shape([[x+b,y+h*.92],[x+w*.48,y+h*.81],[x+w-b,y+h*.90],[x+w-b,y+h-b],[x+b,y+h-b]],'#91a28b',.45);
    shape(rectangle(x,y,w,3),colors.highlight,1,'highlight');
    shape(rectangle(x,y,3,h),colors.highlight,1,'highlight');
    shape([[x-14,y+h+3],[x+w+12,y+h+3],[x+w+23,y+h+12],[x-4,y+h+12]],'#cecfc1');
    shape(rectangle(x-4,y+h+12,w+27,4),'#b6bcad');

    const addPanel = (width,height,map,handle='vertical') => {
      const panel=[];
      const add=(coords,fill,opacity=1,part='')=>panel.push(polygon(coords.map(([u,v])=>map(u,v)),fill,opacity,part));
      // All four corners, glazing and hardware remain on the same rigid plane.
      add(rectangle(1,2,width,height),colors.edge,1,'edge');
      add(rectangle(0,0,width,height),colors.frame,1,'sash');
      add(rectangle(2,2,width-4,2),colors.highlight,1,'highlight');
      add(rectangle(2,2,2,height-4),colors.highlight,1,'highlight');
      add(rectangle(7,7,width-14,height-14),colors.glass,.78,'glass');
      add([[8,8],[width*.64,8],[8,height*.75]],'#ffffff',.27,'reflection');
      add([[width*.79,8],[width-8,8],[width*.25,height-8],[width*.08,height-8]],'#ffffff',.20,'reflection');
      if(handle==='vertical') add(rectangle(width-11,height/2-12,3,24),colors.handle,1,'handle');
      if(handle==='horizontal') add(rectangle(width/2-14,height-11,28,3),colors.handle,1,'handle');
      panels.push(...panel);
    };
    const ix=inside.x, iy=inside.y, iw=inside.width, ih=inside.height;
    if(family==='casement') {
      const theta=a*74*Math.PI/180;
      addPanel(iw,ih,(u,v)=>project(ix+u*Math.cos(theta),iy+v,u*Math.sin(theta)));
    } else if(family==='sliding') {
      const width=iw/2+2;
      addPanel(width,ih,(u,v)=>project(ix+iw-width+u,iy+v),'none');
      addPanel(width,ih,(u,v)=>project(ix+a*(iw-width)+u,iy+v,3));
    } else if(family==='bifold') {
      const width=iw/4, theta=a*82*Math.PI/180;
      for(let i=0;i<4;i++) {
        const sign=i%2===0?1:-1, z=i%2===0?0:width*Math.sin(theta);
        addPanel(width,ih,(u,v)=>project(ix+(i*width+u)*Math.cos(theta),iy+v,z+sign*u*Math.sin(theta)),i===3?'vertical':'none');
      }
    } else {
      const height=ih/2, theta=a*82*Math.PI/180;
      addPanel(iw,height,(u,v)=>project(ix+u,iy+v*Math.cos(theta),v*Math.sin(theta)),'none');
      addPanel(iw,height,(u,v)=>project(ix+u,iy+(height+v)*Math.cos(theta),(height-v)*Math.sin(theta)),'horizontal');
    }
    return {family, amount:a, box, background:opening, panels};
  }
  function polygonMarkup(item) {
    return `<polygon points="${points(item.points)}" fill="${item.fill}" opacity="${item.opacity}"${item.part?` class="motion-${item.part}"`:''}/>`;
  }
  function svg(family,amount=0) {
    const art=geometry(family,amount);
    return `<svg class="window-opening-art" viewBox="0 0 480 400" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg"><g>${art.background.map(polygonMarkup).join('')}</g><g data-moving-panels>${art.panels.map(polygonMarkup).join('')}</g></svg>`;
  }
  function paint(scene,amount) {
    const art=geometry(scene.dataset.motionFamily,amount);
    const nodes=scene.querySelectorAll('[data-moving-panels] polygon');
    art.panels.forEach((item,i)=>nodes[i]?.setAttribute('points',points(item.points)));
  }
  function createController(root,document) {
    const win=document.defaultView, media=win.matchMedia?.('(prefers-reduced-motion: reduce)');
    let paused=false, frame=null, elapsed=0, previous=null, scenes=[], visible=new Set(), lastPaint=-Infinity;
    try { paused=win.sessionStorage.getItem('ena-motion-paused')==='true'; } catch (_) {}
    const reduced=()=>Boolean(media?.matches);
    const stopped=()=>paused||reduced();
    const buttons=()=>root.querySelectorAll('[data-motion-toggle]');
    function state() {
      root.dataset.motionPaused=String(stopped());
      buttons().forEach(button=>{
        button.disabled=reduced();
        button.textContent=reduced()?'Motion off in your settings':paused?'Play animations':'Pause animations';
      });
    }
    function schedule() {
      if(frame==null && !stopped() && !document.hidden && visible.size && win.requestAnimationFrame) frame=win.requestAnimationFrame(tick);
    }
    function stop() {
      if(frame!=null) win.cancelAnimationFrame?.(frame);
      frame=null;previous=null;
    }
    function tick(now) {
      frame=null;
      if(stopped()||document.hidden||!visible.size){previous=null;return;}
      if(previous!=null) elapsed+=Math.min(now-previous,100);
      previous=now;
      // 30fps is ample for deliberate window motion; off-screen cards do no work.
      if(now-lastPaint>=32) {
        const current=cycle(elapsed);
        visible.forEach(scene=>{
          paint(scene,current.amount);
          const label=scene.querySelector('[data-motion-state]');
          if(label && label.textContent!==current.label) label.textContent=current.label;
        });
        lastPaint=now;
      }
      schedule();
    }
    const observer=win.IntersectionObserver ? new win.IntersectionObserver(entries=>{
      entries.forEach(entry=>{if(scenes.includes(entry.target)) entry.isIntersecting?visible.add(entry.target):visible.delete(entry.target);});
      if(!visible.size) stop(); else schedule();
    },{threshold:.05}) : null;
    function refresh() {
      observer?.disconnect();visible.clear();scenes=[...root.querySelectorAll('[data-motion-family]')];
      scenes.forEach(scene=>{
        const current=reduced()?{amount:0,label:'Closed'}:cycle(elapsed);
        paint(scene,current.amount);
        const label=scene.querySelector('[data-motion-state]');if(label)label.textContent=current.label;
        if(observer) observer.observe(scene);else visible.add(scene);
      });
      state();schedule();
    }
    function preferenceChanged() {
      stop();
      if(reduced()) {
        elapsed=0;
        scenes.forEach(scene=>{paint(scene,0);const label=scene.querySelector('[data-motion-state]');if(label)label.textContent='Closed';});
      }
      state();schedule();
    }
    root.addEventListener('click',event=>{
      const button=event.target.closest('[data-motion-toggle]');
      if(!button||reduced())return;
      paused=!paused;
      try {win.sessionStorage.setItem('ena-motion-paused',String(paused));}catch(_){}
      stop();state();schedule();
    });
    media?.addEventListener?.('change',preferenceChanged);
    document.addEventListener('visibilitychange',()=>{stop();schedule();});
    refresh();
    return {refresh};
  }
  const api={families,descriptions,cycle,geometry,svg,createController};
  if(typeof module==='object'&&module.exports)module.exports=api;
  else scope.ENA_WINDOW_ART=api;
})(typeof window==='undefined'?globalThis:window);
